<script setup lang="ts" generic="TProduct extends ProductDetailModalProduct">
  import Button from '@leaflink/stash/Button.vue';
  import Modal from '@leaflink/stash/Modal.vue';
  import findLast from 'lodash-es/findLast';
  import { computed } from 'vue';

  import i18n from '@/plugins/i18n';

  import ProductDetail from './ProductDetail.vue';
  import ProductDetailPricing from './ProductDetailPricing.vue';

  export interface ProductDetailModalProduct {
    displayName: string;
    id: number | string;
    parent?: { id: number | string } | null;
  }

  interface ProductDetailModalProps {
    brandId: number | string;
    parentId: number | string;
    productId: number | string;
    source: string;
    open: boolean;
    brandSlug?: string;
    companySlug?: string;
    isPreview?: boolean;
    isPublicMenu?: boolean;
    productIndex?: number;
    productList?: TProduct[];
  }

  const props = withDefaults(defineProps<ProductDetailModalProps>(), {
    brandSlug: '',
    companySlug: '',
    isPreview: false,
    isPublicMenu: false,
    productIndex: 0,
    productList: () => [],
  });

  const emit = defineEmits<{
    (e: 'bulk-add', payload: { brandId: number | string; productId: number | string; companySlug: string }): void;
    (e: 'change', product: TProduct, index: number): void;
    (e: 'close'): void;
    (e: 'refresh'): void;
  }>();

  const currentProduct = computed(() => {
    return props.productList[productListIndex.value];
  });

  const nextProduct = computed(() => {
    return props.productList.slice(productListIndex.value + 1).find((product) => {
      return !currentProduct.value?.parent || currentProduct.value?.parent.id !== product.parent?.id;
    });
  });

  const previousProduct = computed(() => {
    const prevProduct = findLast(props.productList.slice(0, productListIndex.value), (product) => {
      return !currentProduct.value?.parent || currentProduct.value?.parent.id !== product.parent?.id;
    });

    // If the previous product is a variety,
    // return the first item in the product list that shares the same parent
    return prevProduct?.parent
      ? props.productList.find((product) => product.parent?.id === prevProduct.parent?.id)
      : prevProduct;
  });

  const productListIndex = computed(() => {
    return props.productList.findIndex(
      (product) => product.id === props.productId || product.parent?.id === props.parentId,
    );
  });

  function cancel() {
    emit('close');
  }

  function changeProduct(product: TProduct | undefined, direction = '') {
    if (!product) {
      return;
    }

    const index = direction === 'previous' ? props.productIndex - 1 : props.productIndex + 1;

    emit('change', product, index);
  }

  function onProductAddedToCarts({ shouldCloseModal = false } = {}) {
    emit('refresh');

    if (shouldCloseModal) {
      cancel();
    }
  }

  function onRefreshProduct(product: TProduct) {
    emit('change', product, productListIndex.value);
  }
</script>

<template>
  <div
    @keyup.left="previousProduct && changeProduct(previousProduct, 'previous')"
    @keyup.right="nextProduct && changeProduct(nextProduct, 'next')"
  >
    <Modal
      data-test="modal|product-detail-modal"
      scrollable
      size="wide"
      :open="props.open"
      :title="i18n.t('brand_menu.productDetails')"
      @dismiss="cancel"
    >
      <ProductDetail
        v-if="props.isPublicMenu"
        is-public-menu
        tracking-source="PDP Modal"
        :brand-id="props.brandId"
        :brand-slug="props.brandSlug"
        :company-slug="props.companySlug"
        :is-preview="props.isPreview"
        :product-id="props.parentId || props.productId"
        @close="cancel"
      >
        <template #pricing>
          <slot name="pricing"></slot>
        </template>
      </ProductDetail>

      <ProductDetailPricing
        v-else
        :brand-id="props.brandId"
        :company-slug="props.companySlug"
        :is-preview="props.isPreview"
        :key="props.productId"
        :product-id="props.parentId || props.productId"
        :preselected-variety-id="props.parentId ? props.productId : 0"
        :source="props.source"
        tracking-source="PDP Modal"
        @add="onProductAddedToCarts"
        @bulk-add="
          emit('bulk-add', { brandId: props.brandId, companySlug: props.companySlug, productId: props.productId })
        "
        @change="changeProduct"
        @close="cancel"
        @refresh-product="onRefreshProduct"
      />
      <template #actions>
        <Button
          data-test="modal|button-prev-product"
          inline
          :class="{ [$style.hidden]: !previousProduct }"
          @click="changeProduct(previousProduct, 'previous')"
        >
          {{ i18n.t('brand_menu.previousProduct') }}
        </Button>
        <Button
          v-if="nextProduct"
          data-test="modal|button-next-product"
          inline
          @click="changeProduct(nextProduct, 'next')"
        >
          {{ i18n.t('brand_menu.nextProduct') }}
        </Button>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" module>
  .hidden {
    visibility: hidden;
  }
</style>
